import _startCase from "lodash-es/startCase";
import { useTranslation } from "react-i18next";

import { Table } from "services/databases/getDatabaseTables";
import { useTableNames } from "services/databases/useTableNames";

import { useViewOnly } from "pages/govern/Roles/useViewOnlyContext";

import { useMenu } from "components/ActionMenu/useMenu";
import ContextMenuDivider from "components/ContextMenu/ContextMenuDivider";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";
import { Menu } from "components/LeftSidebar/RecordMenu/RecordMenu";

import { GrantPrivilegesSelector } from "../PrivilegesTable/GrantPrivilegesSelector";
import { GroupInner } from "../PrivilegesTable/GroupInner";
import { ALL_TABLES } from "../PrivilegesTable/constant";
import { usePrivilegesHandlers } from "../PrivilegesTable/usePrivilegesHandlers";
import {
  ROLES_WIZARD_ADD_GROUP_EMPTY,
  ROLES_WIZARD_SET_RESOURCE_OBJECTS,
} from "../privilegesReducer";
import { tablePrivileges } from "../types";
import { usePrivilegesState } from "../usePrivilegesContext";

import groupStyles from "../PrivilegesTable/styles.module.scss";
import styles from "./styles.module.scss";

type Props = {
  index: number;
  resource: string;
  catalogName: string;
};

export const TablesGroup = (props: Props) => {
  const { index, resource, catalogName } = props;
  const { privilegesState, privilegesDispatch } = usePrivilegesState();
  const isViewOnly = useViewOnly();

  const { onGrantPrivileges, deleteGroup, handleMore } = usePrivilegesHandlers({
    index,
    resource,
    catalogName,
  });

  const tableState = privilegesState.databasePrivileges[catalogName]?.table ?? [
    { toAssign: [], toDeny: [], objects: [] },
  ];

  const groupState = tableState[index];
  const { objects, expanded } = groupState;

  const menu = useMenu();
  const tables = useTableNames({ database: catalogName });

  const { t } = useTranslation();

  const menuItems = [
    [
      {
        key: "add_group",
        text: t("roles_wizard.privileges.menu.add"),
        action: () => {
          privilegesDispatch({
            type: ROLES_WIZARD_ADD_GROUP_EMPTY,
            resource,
            objects: [{ name: ALL_TABLES, catalogName }],
            catalogName,
          });
        },
        testId: "roles-wizard-add-tables-group",
        disabled: isViewOnly,
      },
    ],

    ...(tableState.length > 1
      ? [
          [
            {
              key: "delete",
              text: t("roles_wizard.privileges.menu.delete"),
              action: deleteGroup,
              testId: "roles-wizard-delete-tables-group",
              disabled: isViewOnly,
            },
          ],
        ]
      : []),
  ];

  const privilegesOptions = tablePrivileges.map(privilege => {
    return {
      value: privilege,
      text: _startCase(privilege.toLowerCase()),
    };
  });

  const selectedTables = [
    ...new Set<string>(objects.map(object => object.name)),
  ];

  const filterBulkResources = (item: { name: string; catalogName: string }) => {
    if (item.catalogName !== catalogName) {
      return true;
    }
    if (item.catalogName === catalogName) {
      return item.name !== ALL_TABLES;
    }
  };

  const handleSelect = (items: string[]) => {
    const objects = items.map(item => {
      return {
        name: item,
        catalogName,
      };
    });
    privilegesDispatch({
      type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
      resource,
      objects,
      index,
      catalogName,
    });
  };

  const handleResource = (table: { tableName: string }) => {
    const selectedObjects = selectedTables.includes(table.tableName)
      ? objects.filter(object => {
          if (object.catalogName !== catalogName) {
            return true;
          }
          return object.name !== table.tableName;
        })
      : [...objects, { name: table.tableName, catalogName }].filter(
          filterBulkResources
        );

    privilegesDispatch({
      type: ROLES_WIZARD_SET_RESOURCE_OBJECTS,
      resource,
      index,
      catalogName,
      objects: selectedObjects,
    });
  };

  const prefixItems = [
    <ContextMenuItem
      disabled={isViewOnly}
      value={ALL_TABLES}
      key={ALL_TABLES}
      checked={selectedTables.includes(ALL_TABLES)}
      checkedIconPlaceholder={true}
      text={t("roles_wizard.privileges.all_tables")}
      direction="left"
      bulkItem
      skipFilter
      onClick={() => handleSelect([ALL_TABLES])}
    />,
    <ContextMenuDivider key="divider" />,
  ];

  const renderItem = (table: Table) => {
    return (
      <ContextMenuItem
        disabled={isViewOnly}
        key={table.tableName}
        value={table.tableName}
        text={table.tableName}
        direction="left"
        checked={selectedTables.includes(table.tableName)}
        onClick={() => {
          handleResource(table);
        }}
        testId={`table-${table.tableName}`}
      />
    );
  };

  const renderCatalogItem = (name: string) => {
    return <span className={styles.resourceName}>{name}</span>;
  };

  return (
    <div className={groupStyles.group}>
      <GroupInner
        objects={objects}
        expanded={expanded}
        handleMore={handleMore}
        items={tables}
        prefixItems={prefixItems}
        renderCatalogItem={renderCatalogItem}
        renderItem={renderItem}
        resource={resource}
        index={index}
        searchPlaceholder={t("roles_wizard.privileges.search_table")}
        searchKey="tableName"
      />
      <GrantPrivilegesSelector
        groupState={groupState}
        onChange={onGrantPrivileges}
        privileges={privilegesOptions}
        resource={resource}
        index={index}
      />
      <Menu
        items={menuItems}
        menu={menu}
        testId={`roles-wizard-${resource}-${index}-menu`}
        alwaysVisible
      />
    </div>
  );
};
